import { Moment } from 'moment';
import { HdPlanetKey } from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdTransitSegment, HdTransitStation } from '../transit.models';
import { HdTransitInfoCardData } from './hd-transit-info-card.models';

/**
 * Leidt card-data af uit de geladen segmenten van één planeet (popup vanuit de
 * ephemeris-lanes — die kent geen now-next context). `current` is het
 * aangeklikte segment. Verwacht segmenten op oplopende `fromUtc`
 * (service-garantie).
 *
 * - Lijn prev/next: het segment vlak vóór/ná het huidige (bij poort-
 *   granulariteit heeft de kaart geen lijn-rij — `line` is dan `null`).
 * - Poort prev/next: de aaneengesloten run van segmenten met dezelfde poort
 *   rond het huidige segment vormt de huidige poort-container; de segmenten
 *   vlak daarvoor/daarna zijn prev/next poort.
 */
export function segmentsToInfoCardData(
  planetKey: HdPlanetKey,
  segments: readonly HdTransitSegment[],
  current: { readonly gate: number; readonly line: number | null; readonly fromUtc: Moment; readonly toUtc: Moment },
  retrograde: boolean,
  stations: readonly HdTransitStation[] = [],
): HdTransitInfoCardData {
  const fromMs = current.fromUtc.valueOf();
  // Het huidige segment: exacte start-match, anders het segment dat `fromMs` bevat.
  let index = segments.findIndex(segment => segment.fromUtc.valueOf() === fromMs);
  if (index < 0) {
    index = segments.findIndex(
      segment => segment.fromUtc.valueOf() <= fromMs && segment.toUtc.valueOf() > fromMs,
    );
  }

  const prevSegment = index > 0 ? segments[index - 1] : null;
  const nextSegment = index >= 0 && index < segments.length - 1 ? segments[index + 1] : null;

  // De poort-container rond het huidige segment (aaneengesloten zelfde poort).
  let gateStartIndex = index;
  let gateEndIndex = index;
  if (index >= 0) {
    while (gateStartIndex > 0 && segments[gateStartIndex - 1].gate === current.gate) gateStartIndex--;
    while (gateEndIndex < segments.length - 1 && segments[gateEndIndex + 1].gate === current.gate) gateEndIndex++;
  }
  const prevGateSegment = gateStartIndex > 0 ? segments[gateStartIndex - 1] : null;
  const nextGateSegment = gateEndIndex >= 0 && gateEndIndex < segments.length - 1 ? segments[gateEndIndex + 1] : null;
  const currentGateSegment = gateStartIndex >= 0 ? segments[gateStartIndex] : null;

  return {
    planetKey,
    retrograde,
    current: { gate: current.gate, line: current.line, sinceUtc: current.fromUtc },
    currentGate: currentGateSegment != null ? { gate: current.gate, sinceUtc: currentGateSegment.fromUtc } : null,
    prevLine:
      prevSegment != null && prevSegment.line != null
        ? { atUtc: prevSegment.fromUtc, gate: prevSegment.gate, line: prevSegment.line }
        : null,
    nextLine:
      nextSegment != null && nextSegment.line != null
        ? { atUtc: nextSegment.fromUtc, gate: nextSegment.gate, line: nextSegment.line }
        : null,
    prevGate: prevGateSegment != null ? { atUtc: prevGateSegment.fromUtc, gate: prevGateSegment.gate } : null,
    nextGate: nextGateSegment != null ? { atUtc: nextGateSegment.fromUtc, gate: nextGateSegment.gate } : null,
    fromUtc: current.fromUtc,
    toUtc: current.toUtc,
    stations,
  };
}
